import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { Mail, Phone, Calendar, Check, Trash2, MessageSquare } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMessagesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/contact`, {
        headers: getAuthHeaders()
      });
      setMessages(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await axios.put(`${API}/contact/${id}/read`, {}, {
        headers: getAuthHeaders()
      });
      setMessages(messages.map(m => 
        m.id === id ? { ...m, is_read: true } : m
      ));
      if (selectedMessage?.id === id) {
        setSelectedMessage({ ...selectedMessage, is_read: true });
      }
      toast.success('Marqué comme lu');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;

    try {
      await axios.delete(`${API}/contact/${id}`, {
        headers: getAuthHeaders()
      });
      setMessages(messages.filter(m => m.id !== id));
      if (selectedMessage?.id === id) {
        setSelectedMessage(null);
      }
      toast.success('Message supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl text-[#3D2E35]">Messages</h1>
          <p className="text-[#6B5A62] mt-1">
            {unreadCount > 0 
              ? `${unreadCount} message${unreadCount > 1 ? 's' : ''} non lu${unreadCount > 1 ? 's' : ''}`
              : 'Tous les messages sont lus'
            }
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#8B4B7C] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl">
            <MessageSquare className="mx-auto text-[#E8A5C4]" size={48} />
            <p className="text-[#6B5A62] mt-4">Aucun message pour le moment</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Messages List */}
            <div className="lg:col-span-1 space-y-3">
              {messages.map((message) => (
                <div
                  key={message.id}
                  onClick={() => {
                    setSelectedMessage(message);
                    if (!message.is_read) {
                      handleMarkAsRead(message.id);
                    }
                  }}
                  className={`bg-white rounded-xl p-4 cursor-pointer transition-all ${
                    selectedMessage?.id === message.id 
                      ? 'ring-2 ring-[#8B4B7C]' 
                      : 'hover:shadow-md'
                  } ${!message.is_read ? 'border-l-4 border-[#C45B9F]' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-[#3D2E35] truncate ${!message.is_read ? 'font-semibold' : ''}`}>
                        {message.name}
                      </p>
                      <p className="text-sm text-[#8B4B7C] truncate">{message.subject}</p>
                      <p className="text-xs text-[#6B5A62] mt-1 truncate">{message.message}</p>
                    </div>
                    {!message.is_read && (
                      <span className="w-2 h-2 rounded-full bg-[#C45B9F] flex-shrink-0 mt-2"></span>
                    )}
                  </div>
                  <p className="text-xs text-[#6B5A62] mt-2">
                    {formatDate(message.created_at)}
                  </p>
                </div>
              ))}
            </div>

            {/* Message Detail */}
            <div className="lg:col-span-2">
              {selectedMessage ? (
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="font-heading text-xl text-[#3D2E35]">{selectedMessage.subject}</h2>
                      <p className="text-sm text-[#6B5A62] mt-1">
                        {formatDate(selectedMessage.created_at)}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      {!selectedMessage.is_read && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkAsRead(selectedMessage.id)}
                        >
                          <Check size={16} className="mr-1" />
                          Marquer lu
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => handleDelete(selectedMessage.id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>

                  {/* Sender Info */}
                  <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-[#E8A5C4]/20">
                    <div className="flex items-center text-[#6B5A62]">
                      <div className="w-10 h-10 rounded-full bg-[#8B4B7C] flex items-center justify-center text-white font-medium mr-3">
                        {selectedMessage.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-[#3D2E35]">{selectedMessage.name}</p>
                        <div className="flex items-center text-sm">
                          <Mail size={14} className="mr-1" />
                          <a href={`mailto:${selectedMessage.email}`} className="hover:text-[#8B4B7C]">
                            {selectedMessage.email}
                          </a>
                        </div>
                      </div>
                    </div>
                    {selectedMessage.phone && (
                      <div className="flex items-center text-[#6B5A62]">
                        <Phone size={16} className="mr-2" />
                        <a href={`tel:${selectedMessage.phone}`} className="hover:text-[#8B4B7C]">
                          {selectedMessage.phone}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Message Content */}
                  <div className="prose prose-sm max-w-none">
                    <p className="text-[#3D2E35] whitespace-pre-wrap">{selectedMessage.message}</p>
                  </div>

                  {/* Quick Actions */}
                  <div className="mt-6 pt-6 border-t border-[#E8A5C4]/20 flex space-x-3">
                    <a
                      href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                      className="btn-tevaite inline-flex items-center px-4 py-2 rounded-lg text-white text-sm"
                    >
                      <Mail size={16} className="mr-2" />
                      Répondre par email
                    </a>
                    {selectedMessage.phone && (
                      <a
                        href={`tel:${selectedMessage.phone}`}
                        className="btn-tevaite-outline inline-flex items-center px-4 py-2 rounded-lg text-sm"
                      >
                        <Phone size={16} className="mr-2" />
                        Appeler
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-12 text-center">
                  <MessageSquare className="mx-auto text-[#E8A5C4]" size={48} />
                  <p className="text-[#6B5A62] mt-4">Sélectionnez un message pour le lire</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMessagesPage;
